import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const pseudoRandom = (seed: number) => {
  const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};

// 碎片化转场：破茧成蝶
export const FragmentedTransition: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // === 三幕结构 ===
  // Act 1: 茧 (0–90)   ── 蜕变前的宁静
  // Act 2: 碎 (85–185)  ── 世界碎裂、光从裂缝涌入
  // Act 3: 蝶 (180–300) ── 碎片重聚、蝶翼展开

  const gridCols = 10;
  const gridRows = 7;
  const pieceWidth = width / gridCols;
  const pieceHeight = height / gridRows;

  // ─── Act 1 ─── 标题/副标题/装饰 进入动画
  const act1TitleSpring = spring({
    frame,
    fps,
    config: { damping: 14, stiffness: 80, mass: 0.6 },
  });
  const act1SubSpring = spring({
    frame: Math.max(0, frame - 20),
    fps,
    config: { damping: 18, stiffness: 60 },
  });
  const act1DotSpring = spring({
    frame: Math.max(0, frame - 40),
    fps,
    config: { damping: 12, stiffness: 100 },
  });
  const act1Opacity = interpolate(frame, [72, 92], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // ─── Act 2 ─── 碎片化
  const fragmentProgress = interpolate(frame, [85, 170], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const fragmentLayerOpacity = interpolate(
    frame,
    [85, 95, 170, 188],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );
  const lightIntensity = interpolate(
    frame,
    [105, 145, 175, 200],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  // ─── Act 3 ─── 新场景
  const act3Opacity = interpolate(frame, [180, 205], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const act3TitleSpring = spring({
    frame: Math.max(0, frame - 200),
    fps,
    config: { damping: 14, stiffness: 80, mass: 0.6 },
  });
  const act3SubSpring = spring({
    frame: Math.max(0, frame - 225),
    fps,
    config: { damping: 18, stiffness: 60 },
  });
  const act3DotSpring = spring({
    frame: Math.max(0, frame - 248),
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  // ─── 背景色渐变（暗蓝 → 深紫 → 暖紫粉）───
  const bgHue = interpolate(frame, [0, 150, 300], [220, 270, 320]);
  const bgLight = interpolate(frame, [0, 120, 200, 300], [11, 7, 14, 17], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // ─── 生成碎片 ───
  const pieces: {
    idx: number;
    x: number;
    y: number;
    rotate: number;
    opacity: number;
    scale: number;
    hue: number;
  }[] = [];

  for (let row = 0; row < gridRows; row++) {
    for (let col = 0; col < gridCols; col++) {
      const idx = row * gridCols + col;
      const baseX = col * pieceWidth;
      const baseY = row * pieceHeight;

      const cx = (col - gridCols / 2) / gridCols;
      const cy = (row - gridRows / 2) / gridRows;
      const dist = Math.sqrt(cx * cx + cy * cy);
      const delay = dist * 0.3;
      const safeDelay = Math.max(0.01, delay);

      const angle = Math.atan2(cy, cx);
      const r1 = pseudoRandom(idx);
      const explodeDist = 300 + r1 * 400;

      const localProg = interpolate(
        fragmentProgress,
        [safeDelay, Math.min(safeDelay + 0.5, 1)],
        [0, 1],
        { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
      );

      pieces.push({
        idx,
        x: interpolate(localProg, [0, 1], [baseX, baseX + Math.cos(angle) * explodeDist]),
        y: interpolate(localProg, [0, 1], [baseY, baseY + Math.sin(angle) * explodeDist]),
        rotate: interpolate(localProg, [0, 1], [0, (r1 - 0.5) * 720]),
        opacity: interpolate(localProg, [0, 0.3, 1], [1, 0.75, 0.15], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        }),
        scale: interpolate(localProg, [0, 0.2, 1], [1, 1.12, 0.55], {
          extrapolateLeft: 'clamp',
          extrapolateRight: 'clamp',
        }),
        hue: interpolate(idx, [0, gridRows * gridCols], [200, 340]),
      });
    }
  }

  // ─── 漂浮粒子 ───
  const particles = Array.from({ length: 28 }, (_, i) => {
    const s1 = pseudoRandom(i + 100);
    const s2 = pseudoRandom(i + 200);
    const prog = interpolate(frame, [92 + i * 2.5, 192 + i * 2.5], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
    return {
      x: width * s1 + Math.sin(frame * 0.04 + i) * 60,
      y: height * s2 + Math.cos(frame * 0.03 + i) * 60,
      size: 3 + s1 * 5,
      opacity: Math.sin(prog * Math.PI) * 0.75,
      hue: 200 + s1 * 140,
    };
  });

  // ─── 阶段指示标签 ───
  const phases = ['茧', '碎', '蝶'] as const;
  const activePhaseIdx = frame < 88 ? 0 : frame < 183 ? 1 : 2;

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(135deg, hsl(${bgHue},38%,${bgLight}%) 0%, hsl(${bgHue + 30},34%,${bgLight + 3}%) 50%, hsl(${bgHue + 60},28%,${bgLight + 6}%) 100%)`,
        overflow: 'hidden',
        fontFamily: '"PingFang SC","Microsoft YaHei","Noto Sans SC",sans-serif',
      }}
    >
      {/* ════════ ACT 1 · 茧 ════════ */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: act1Opacity,
        }}
      >
        {/* 同心光环 */}
        {[0, 1, 2].map((i) => {
          const ringSpring = spring({
            frame: Math.max(0, frame - i * 12),
            fps,
            config: { damping: 22, stiffness: 50 },
          });
          const size = 300 + i * 130;
          return (
            <div
              key={`ring-${i}`}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: size,
                height: size,
                borderRadius: '50%',
                border: `1px solid rgba(120,200,255,${0.16 - i * 0.04})`,
                transform: `translate(-50%,-50%) scale(${ringSpring})`,
                opacity: ringSpring * 0.6,
              }}
            />
          );
        })}

        {/* 标题 */}
        <div
          style={{
            position: 'absolute',
            top: '37%',
            left: '50%',
            transform: `translate(-50%,-50%) translateY(${interpolate(act1TitleSpring, [0, 1], [60, 0])}px)`,
            opacity: act1TitleSpring,
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              fontSize: 74,
              fontWeight: 900,
              color: '#fff',
              margin: 0,
              letterSpacing: 14,
              textShadow: '0 0 40px rgba(100,200,255,0.55)',
            }}
          >
            每一次蜕变
          </h1>
        </div>

        {/* 副标题 */}
        <div
          style={{
            position: 'absolute',
            top: '52%',
            left: '50%',
            transform: `translate(-50%,0) translateY(${interpolate(act1SubSpring, [0, 1], [36, 0])}px)`,
            opacity: act1SubSpring,
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: 32,
              color: 'rgba(180,220,255,0.85)',
              margin: 0,
              letterSpacing: 6,
            }}
          >
            都要经历一次勇敢的破碎
          </p>
        </div>

        {/* 底部圆点装饰 */}
        <div
          style={{
            position: 'absolute',
            bottom: 80,
            left: '50%',
            transform: `translateX(-50%) scale(${act1DotSpring})`,
            opacity: act1DotSpring,
            display: 'flex',
            gap: 28,
            alignItems: 'center',
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                width: i === 2 ? 14 : 7,
                height: i === 2 ? 14 : 7,
                background: 'rgba(120,200,255,0.6)',
                borderRadius: '50%',
                boxShadow: '0 0 14px rgba(120,200,255,0.35)',
              }}
            />
          ))}
        </div>
      </div>

      {/* ════════ ACT 2 · 碎 ════════ */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: fragmentLayerOpacity,
        }}
      >
        {/* 碎片 */}
        {pieces.map((p) => (
          <div
            key={p.idx}
            style={{
              position: 'absolute',
              width: pieceWidth - 2,
              height: pieceHeight - 2,
              left: p.x,
              top: p.y,
              background: `hsla(${p.hue},58%,48%,${p.opacity * 0.22})`,
              border: `1px solid hsla(${p.hue},58%,68%,${p.opacity * 0.38})`,
              transform: `rotate(${p.rotate}deg) scale(${p.scale})`,
              boxShadow: `0 0 14px hsla(${p.hue},58%,48%,${p.opacity * 0.28})`,
            }}
          />
        ))}

        {/* 光线从裂缝涌出 */}
        {lightIntensity > 0 && (
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {Array.from({ length: 6 }, (_, i) => {
              const rayAngle = i * 60 + frame * 0.35;
              return (
                <div
                  key={`ray-${i}`}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: 2.5,
                    height: 550 * lightIntensity,
                    background: `linear-gradient(to bottom, rgba(255,255,255,${lightIntensity * 0.55}), transparent)`,
                    transform: `translate(-50%,-50%) rotate(${rayAngle}deg)`,
                    transformOrigin: 'center center',
                  }}
                />
              );
            })}
            {/* 中心辉光 */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 220 * lightIntensity,
                height: 220 * lightIntensity,
                borderRadius: '50%',
                background: `radial-gradient(circle, rgba(255,255,255,${lightIntensity * 0.35}) 0%, transparent 70%)`,
                transform: 'translate(-50%,-50%)',
              }}
            />
          </div>
        )}

        {/* 漂浮粒子 */}
        {particles.map((p, i) =>
          p.opacity > 0.01 ? (
            <div
              key={`pt-${i}`}
              style={{
                position: 'absolute',
                left: p.x,
                top: p.y,
                width: p.size,
                height: p.size,
                borderRadius: '50%',
                background: `hsla(${p.hue},75%,68%,${p.opacity})`,
                boxShadow: `0 0 ${p.size * 2}px hsla(${p.hue},75%,68%,${p.opacity * 0.5})`,
              }}
            />
          ) : null,
        )}
      </div>

      {/* ════════ ACT 3 · 蝶 ════════ */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: act3Opacity,
        }}
      >
        {/* 重聚光环 */}
        {[0, 1, 2].map((i) => {
          const ringSpring = spring({
            frame: Math.max(0, frame - 195 - i * 10),
            fps,
            config: { damping: 18, stiffness: 55 },
          });
          const size = 260 + i * 140;
          return (
            <div
              key={`re-ring-${i}`}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: size,
                height: size,
                borderRadius: '50%',
                border: `1px solid rgba(255,160,255,${0.18 - i * 0.05})`,
                transform: `translate(-50%,-50%) scale(${ringSpring})`,
                opacity: ringSpring * 0.65,
              }}
            />
          );
        })}

        {/* 标题 */}
        <div
          style={{
            position: 'absolute',
            top: '34%',
            left: '50%',
            transform: `translate(-50%,-50%) translateY(${interpolate(act3TitleSpring, [0, 1], [60, 0])}px)`,
            opacity: act3TitleSpring,
            textAlign: 'center',
          }}
        >
          <h1
            style={{
              fontSize: 82,
              fontWeight: 900,
              color: '#fff',
              margin: 0,
              letterSpacing: 18,
              textShadow:
                '0 0 50px rgba(255,150,255,0.65), 0 0 100px rgba(255,100,200,0.25)',
            }}
          >
            破茧成蝶
          </h1>
        </div>

        {/* 副标题 */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: `translate(-50%,0) translateY(${interpolate(act3SubSpring, [0, 1], [36, 0])}px)`,
            opacity: act3SubSpring,
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: 32,
              color: 'rgba(255,210,255,0.9)',
              margin: 0,
              letterSpacing: 5,
              lineHeight: 1.8,
            }}
          >
            更好的自己 · 正在路上
          </p>
        </div>

        {/* 底部圆点 */}
        <div
          style={{
            position: 'absolute',
            bottom: 80,
            left: '50%',
            transform: `translateX(-50%) scale(${act3DotSpring})`,
            opacity: act3DotSpring,
            display: 'flex',
            gap: 24,
            alignItems: 'center',
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                width: i === 2 ? 14 : 7,
                height: i === 2 ? 14 : 7,
                background: `hsla(${300 + i * 14},65%,68%,0.8)`,
                borderRadius: '50%',
                boxShadow: `0 0 14px hsla(${300 + i * 14},65%,68%,0.35)`,
              }}
            />
          ))}
        </div>

        {/* 尾声引言 */}
        {frame > 262 && (
          <div
            style={{
              position: 'absolute',
              bottom: 130,
              width: '100%',
              textAlign: 'center',
              opacity: interpolate(frame, [262, 282], [0, 1], {
                extrapolateLeft: 'clamp',
                extrapolateRight: 'clamp',
              }),
            }}
          >
            <p
              style={{
                fontSize: 26,
                color: 'rgba(255,255,255,0.45)',
                margin: 0,
                fontStyle: 'italic',
                letterSpacing: 2,
              }}
            >
              "万物皆有裂痕，那是光照进来的地方"
            </p>
          </div>
        )}
      </div>

      {/* ════════ 阶段指示器 ════════ */}
      <div
        style={{
          position: 'absolute',
          top: 36,
          right: 36,
          display: 'flex',
          gap: 8,
          opacity: interpolate(frame, [0, 18], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          }),
        }}
      >
        {phases.map((label, i) => {
          const active = i === activePhaseIdx;
          return (
            <div
              key={label}
              style={{
                padding: '7px 18px',
                fontSize: 15,
                color: active ? '#fff' : 'rgba(255,255,255,0.28)',
                background: active ? 'rgba(255,255,255,0.13)' : 'transparent',
                borderRadius: 20,
                border: `1px solid ${active ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.08)'}`,
                letterSpacing: 5,
              }}
            >
              {label}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
